"use client";

import { useState, useEffect, useRef } from "react";

const CHECK_INTERVAL_MS = 90_000; // 90 seconds

/**
 * Polls /_next/static/BUILD_ID/_buildManifest.js for 404 → new deploy detected.
 * Only runs in production (dev builds always "miss" because the build ID
 * is Date.now() and never matches the actual Next.js chunk paths).
 */
export function NewVersionBanner() {
    const [hasUpdate, setHasUpdate] = useState(false);
    const checkedRef = useRef(false);

    useEffect(() => {
        // Never run in development — build IDs don't work the same way
        if (process.env.NODE_ENV !== "production") return;

        const el = document.querySelector<HTMLMetaElement>('meta[name="build-id"]');
        const buildId = el?.content;
        if (!buildId) return;

        const check = async () => {
            try {
                const res = await fetch(
                    `/_next/static/${buildId}/_buildManifest.js`,
                    { method: "HEAD", cache: "no-store" }
                );
                // 404 means this build's manifest is gone → new deployment is live
                if (res.status === 404) setHasUpdate(true);
            } catch {
                /* network error → don't falsely trigger */
            }
        };

        const onFocus = () => {
            if (!checkedRef.current) { checkedRef.current = true; return; }
            check();
        };

        window.addEventListener("focus", onFocus);
        const interval = setInterval(check, CHECK_INTERVAL_MS);

        return () => {
            window.removeEventListener("focus", onFocus);
            clearInterval(interval);
        };
    }, []);

    if (!hasUpdate) return null;

    return (
        <div
            role="alert"
            style={{
                position: "fixed",
                top: "12px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 10000,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "7px 12px 7px 10px",
                borderRadius: "999px",
                background: "linear-gradient(135deg, #92400e, #78350f)",
                color: "#fef3c7",
                fontSize: "12px",
                fontWeight: 600,
                fontFamily: "'Inter', system-ui, sans-serif",
                boxShadow: "0 4px 18px rgba(120,53,15,0.55), 0 0 0 1px rgba(251,191,36,0.25)",
                animation: "nvSlideIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
                whiteSpace: "nowrap",
                border: "1px solid rgba(251,191,36,0.3)",
            }}
        >
            <span style={{ fontSize: "12px" }}>⚡</span>
            <span style={{ color: "#fde68a" }}>New updates</span>
            <button
                onClick={() => window.location.reload()}
                style={{
                    padding: "3px 10px",
                    borderRadius: "999px",
                    background: "rgba(251,191,36,0.18)",
                    border: "1px solid rgba(251,191,36,0.4)",
                    color: "#fde68a",
                    fontSize: "11px",
                    fontWeight: 700,
                    cursor: "pointer",
                    letterSpacing: "0.02em",
                }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.background = "rgba(251,191,36,0.32)")}
                onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.background = "rgba(251,191,36,0.18)")}
            >
                Refresh
            </button>
            <style>{`
        @keyframes nvSlideIn {
          from { opacity: 0; transform: translateY(-8px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
        </div>
    );
}
